import React from 'react';
import { ExtapiInvoice } from '../../../Types/MyPages/InvoicesModel';
import { Link } from 'react-router-dom';
import { translate } from '../../../Services/translation';
import Icon from '../../Icon';
import clsx from 'clsx';

interface MyPagesInvoiceListProps {
  isLoading: boolean;
  onInvoiceClick?: (invoice: ExtapiInvoice) => void;
  onDownloadInvoice?: (invoice: ExtapiInvoice) => void;
  invoices?: ExtapiInvoice[];
  hasError?: boolean;
  showDownload?: boolean;
  invoiceButtonText?: string;
  invoiceClassName?: (invoice: ExtapiInvoice) => string;
}

const MyPagesInvoiceList = ({
  isLoading,
  hasError,
  invoices,
  onInvoiceClick,
  onDownloadInvoice,
  invoiceButtonText,
  invoiceClassName,
  showDownload = true,
}: MyPagesInvoiceListProps) => {
  return (
    <>
      {/* Mobile */}
      <ul className="max-h-[648px] overflow-y-auto pr-2 lg:hidden">
        {!isLoading &&
          !hasError &&
          invoices?.map((invoice) => (
            <li
              key={`invoice-mobile-${invoice.documentNo}`}
              className={clsx(
                'mb-4 rounded border border-solid border-gray-300 bg-white p-4',
                invoiceClassName && invoiceClassName(invoice)
              )}
            >
              <div className="mb-3 flex justify-between">
                <p className="text-md font-bold">
                  {translate('mypage.rma.invoicenumber')}:{' '}
                  {invoice.documentNo}
                </p>
                <div className="flex items-center gap-1">
                  <span className="text-sm">
                    {translate('mypage.invoices.open')}
                  </span>
                  <Icon
                    name={invoice.open ? 'cancel' : 'check'}
                    className={clsx(
                      'text-lg',
                      invoice.open && 'opacity-75'
                    )}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5">
                <div className="flex flex-col">
                  <p className="text-sm uppercase text-gray-600">
                    {translate('mypage.rma.ordernumber')}
                  </p>
                  <p className="text-md">{invoice.orderNo}</p>
                </div>
                <div className="flex flex-col">
                  <p className="text-sm uppercase text-gray-600">
                    {translate('mypage.rma.yourordernumber')}
                  </p>
                  <p className="text-md">{invoice.yourOrderNo}</p>
                </div>
                <div className="flex flex-col">
                  <p className="text-sm uppercase text-gray-600">
                    {translate('mypage.rma.postingdate')}
                  </p>
                  <p className="text-md">{invoice.postingDate}</p>
                </div>
                <div className="flex flex-col">
                  <p className="text-sm uppercase text-gray-600">
                    {translate('mypage.rma.amount')}
                  </p>
                  <p className="text-md">{invoice.originalAmount}</p>
                </div>
                <div className="flex flex-col">
                  <p className="text-sm uppercase text-gray-600">
                    {translate('mypage.rma.duedate')}
                  </p>
                  <p className="text-md">{invoice.dueDate}</p>
                </div>
              </div>
              <div className="mt-3 flex w-full gap-2">
                {!onInvoiceClick && (
                  <Link
                    to={invoice.documentNo}
                    className="btn btn--primary btn--small col-span-2 flex-1 font-bold md:col-span-1"
                  >
                    {translate('general.read-more')}
                  </Link>
                )}
                {onInvoiceClick && (
                  <button
                    className="btn btn--primary btn--small col-span-2 flex-1 font-bold md:col-span-1"
                    onClick={() => onInvoiceClick(invoice)}
                  >
                    {invoiceButtonText ?? translate('general.read-more')}
                  </button>
                )}
                {showDownload && (
                  <button
                    className="btn btn--outline btn--small col-span-2 flex flex-1 items-center justify-center gap-2 font-bold md:col-span-1"
                    onClick={() =>
                      typeof onDownloadInvoice === 'function' &&
                      onDownloadInvoice(invoice)
                    }
                    disabled={!invoice.canDownload}
                  >
                    <Icon name="file-arrow-down" className="h-5 w-5" />
                    {translate('general.download')}
                  </button>
                )}
              </div>
            </li>
          ))}
        {isLoading &&
          Array.from({ length: 3 }).map((_, idx) => (
            <li
              key={`invoice-mobile-${idx}`}
              className="mb-4 flex flex-col gap-2 rounded border border-solid border-gray-300 bg-white p-4"
            >
              <span className="skeleton max-w-[40%] rounded-sm bg-gray-200">
                &nbsp;
              </span>
              <div className="grid grid-cols-2 md:grid-cols-5">
                <div className="flex max-w-[40%] flex-col gap-[2px]">
                  <span className="skeleton rounded-sm bg-gray-200">
                    &nbsp;
                  </span>
                  <span className="skeleton rounded-sm bg-gray-200">
                    &nbsp;
                  </span>
                </div>
                <div className="flex max-w-[40%] flex-col gap-[2px]">
                  <span className="skeleton rounded-sm bg-gray-200">
                    &nbsp;
                  </span>
                  <span className="skeleton rounded-sm bg-gray-200">
                    &nbsp;
                  </span>
                </div>
                <div className="flex max-w-[40%] flex-col gap-[2px]">
                  <span className="skeleton rounded-sm bg-gray-200">
                    &nbsp;
                  </span>
                  <span className="skeleton rounded-sm bg-gray-200">
                    &nbsp;
                  </span>
                </div>
                <div className="flex max-w-[40%] flex-col gap-[2px]">
                  <span className="skeleton rounded-sm bg-gray-200">
                    &nbsp;
                  </span>
                  <span className="skeleton rounded-sm bg-gray-200">
                    &nbsp;
                  </span>
                </div>
              </div>
            </li>
          ))}
      </ul>
      {/* Desktop */}
      {!isLoading && !hasError && (
        <ul className="hidden max-h-[648px] overflow-y-auto rounded border border-solid border-gray-300 bg-white lg:block">
          <li className="grid grid-cols-[108px_minmax(0,_1fr)_minmax(0,_1fr)_minmax(0,_1fr)_minmax(0,_1fr)_minmax(0,_1fr)_minmax(0,_1fr)_minmax(0,_1fr)] gap-4 rounded-t border-b border-solid border-gray-300 p-2">
            <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-bold">
              {translate('mypage.invoices.open')}
            </p>
            <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-bold">
              {translate('mypage.rma.invoicenumber')}
            </p>
            <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-bold">
              {translate('mypage.rma.ordernumber')}
            </p>
            <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-bold">
              {translate('mypage.rma.yourordernumber')}
            </p>
            <p className="overflow-hidden text-ellipsis whitespace-nowrap text-right text-sm font-bold">
              {translate('mypage.rma.postingdate')}
            </p>
            <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-bold">
              {translate('mypage.rma.amount')}
            </p>
            <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-bold">
              {translate('mypage.rma.duedate')}
            </p>
          </li>
          {!isLoading &&
            invoices?.map((invoice) => (
              <li key={`invoice-${invoice.documentNo}`}>
                <Link
                  className={clsx(
                    'grid grid-cols-[108px_minmax(0,_1fr)_minmax(0,_1fr)_minmax(0,_1fr)_minmax(0,_1fr)_minmax(0,_1fr)_minmax(0,_1fr)_minmax(0,_1fr)] gap-4 border border-solid border-gray-300 p-2 text-sm [&:last-child]:rounded-b [&:last-child]:border-b-0 [&:nth-child(even)]:bg-primary-50 [&:nth-child(odd)]:bg-white',
                    invoiceClassName && invoiceClassName(invoice)
                  )}
                  to={invoice.documentNo}
                  onClick={(e) => {
                    if (typeof onInvoiceClick === 'function') {
                      e.preventDefault();
                      onInvoiceClick(invoice);
                    }
                  }}
                >
                  <span className="text-base">
                    <Icon
                      name={invoice.open ? 'cancel' : 'check'}
                      size="lg"
                      className={clsx(invoice.open && 'opacity-75')}
                    />
                  </span>
                  <span
                    className={clsx(
                      invoice['documentType'] == 'Credit Memo' &&
                        'text-red-600'
                    )}
                  >
                    {invoice.documentNo}
                  </span>
                  <span>{invoice.orderNo}</span>
                  <span>{invoice.yourOrderNo}</span>
                  <span className="text-right">{invoice.postingDate}</span>
                  <span>{invoice.originalAmount}</span>
                  <span>{invoice.dueDate}</span>
                  <span>
                    {showDownload && invoice.canDownload && (
                      <button
                        onClick={() =>
                          typeof onDownloadInvoice === 'function' &&
                          onDownloadInvoice(invoice)
                        }
                        disabled={!invoice.canDownload}
                        className="opacity-50 hover:opacity-100 disabled:hover:opacity-50"
                      >
                        <Icon
                          name="file-arrow-down"
                          size="lg"
                          className="pointer-events-none"
                        />
                      </button>
                    )}
                  </span>
                </Link>
              </li>
            ))}
          {isLoading &&
            Array.from({ length: 5 }).map((_, idx) => (
              <li
                key={'invoice-skeleton-' + idx}
                className="skeleton grid grid-cols-[108px_minmax(0,_1fr)_minmax(0,_1fr)_minmax(0,_1fr)_minmax(0,_1fr)_minmax(0,_1fr)_minmax(0,_1fr)_minmax(0,_1fr)] gap-4 border-b border-solid border-gray-300 bg-white px-2 py-3 [&_:last-child]:border-b-0"
              >
                <div className="skeleton bg-gray-100">&nbsp;</div>
                <div className="skeleton bg-gray-100">&nbsp;</div>
                <div className="skeleton bg-gray-100">&nbsp;</div>
                <div className="skeleton bg-gray-100">&nbsp;</div>
                <div className="skeleton bg-gray-100">&nbsp;</div>
                <div className="skeleton bg-gray-100">&nbsp;</div>
                <div className="skeleton bg-gray-100">&nbsp;</div>
                <div className="skeleton bg-gray-100">&nbsp;</div>
              </li>
            ))}
        </ul>
      )}
    </>
  );
};

export default MyPagesInvoiceList;
